import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, UploadCloud, Pencil, Trash2, Star, ScanFace, ExternalLink, Hash } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { EventForm } from "@/components/EventForm";
import { Progress } from "@/components/ui/progress";
import { api, brl, fmtDate, thumbUrl, errMsg, CATEGORIES } from "@/lib/api";

export default function AdminEvent() {
  const { id } = useParams();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [edit, setEdit] = useState(false);
  const [upload, setUpload] = useState(null);
  const [drag, setDrag] = useState(false);

  const load = () => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data));
    api.get(`/events/${id}/photos`).then((r) => setPhotos(r.data));
  };
  useEffect(load, [id]);

  const uploadFiles = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    let ok = 0, fail = 0;
    for (let i = 0; i < list.length; i++) {
      setUpload({ done: i, total: list.length });
      try {
        const fd = new FormData();
        fd.append("file", list[i]);
        const { data } = await api.post(`/admin/events/${id}/photos`, fd);
        setPhotos((p) => [data, ...p]);
        ok++;
      } catch (e) {
        fail++;
        toast.error(`${list[i].name}: ${errMsg(e)}`);
      }
    }
    setUpload(null);
    toast.success(`${ok} foto(s) enviada(s)${fail ? `, ${fail} com erro` : ""}. Lendo números de peito…`);
    load();
    setTimeout(load, 10000);
    setTimeout(load, 25000);
  };

  const delPhoto = async (pid) => {
    await api.delete(`/admin/photos/${pid}`);
    setPhotos((p) => p.filter((x) => x.id !== pid));
  };
  const setCover = async (pid) => {
    await api.post(`/admin/events/${id}/cover/${pid}`);
    setEvent({ ...event, cover_photo_id: pid });
    toast.success("Capa definida");
  };
  const editBibs = async (p) => {
    const v = window.prompt("Números de peito desta foto (separados por vírgula):", (p.bibs || []).join(", "));
    if (v === null) return;
    const { data } = await api.put(`/admin/photos/${p.id}/bibs`, { bibs: v.split(/[,\s]+/) });
    setPhotos((list) => list.map((x) => (x.id === p.id ? { ...x, bibs: data.bibs, bibs_status: "done" } : x)));
  };
  const delEvent = async () => {
    if (!window.confirm("Excluir este evento?")) return;
    await api.delete(`/admin/events/${id}`);
    nav("/admin");
  };

  if (!event) return <AdminShell><p className="text-zinc-500">Carregando…</p></AdminShell>;
  const faces = photos.reduce((s, p) => s + (p.face_count || 0), 0);

  return (
    <AdminShell>
      <Link to="/admin" className="text-sm text-zinc-500 flex items-center gap-1 hover:text-zinc-900" data-testid="admin-back-link"><ArrowLeft className="w-4 h-4" /> Voltar</Link>
      <div className="mt-3 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className="label-caps text-zinc-500">{CATEGORIES[event.category]}</span>
          <h1 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tighter" data-testid="admin-event-title">{event.name}</h1>
          <p className="mt-1 text-sm text-zinc-500">{fmtDate(event.date)} · {event.location || "Sem local"} · {brl(event.price)} por foto · pacote {brl(event.package_price)} · {photos.length} fotos · {faces} rostos detectados</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/evento/${id}`} data-testid="admin-open-public-btn" className="h-10 px-3 rounded-md border border-zinc-200 bg-white text-sm flex items-center gap-1.5"><ExternalLink className="w-4 h-4" /> Página pública</Link>
          <button onClick={() => setEdit(true)} data-testid="edit-event-btn" className="h-10 px-3 rounded-md border border-zinc-200 bg-white text-sm flex items-center gap-1.5"><Pencil className="w-4 h-4" /> Editar</button>
          <button onClick={delEvent} data-testid="delete-event-btn" className="h-10 px-3 rounded-md border border-red-200 text-red-600 bg-white text-sm flex items-center gap-1.5"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); uploadFiles(e.dataTransfer.files); }}
        onClick={() => !upload && fileRef.current?.click()}
        data-testid="photo-dropzone"
        className={`mt-8 cursor-pointer border-2 border-dashed rounded-lg p-10 text-center transition-colors ${drag ? "border-[#0055FF] bg-[#0055FF]/5" : "border-zinc-300 bg-white hover:border-zinc-900"}`}
      >
        <UploadCloud className="w-10 h-10 mx-auto text-[#0055FF]" />
        <p className="mt-3 font-semibold">Arraste as fotos aqui ou clique para selecionar</p>
        <p className="text-sm text-zinc-500">Você pode enviar várias de uma vez. Os rostos são detectados automaticamente.</p>
        {upload && (
          <div className="mt-5 max-w-md mx-auto" data-testid="upload-progress">
            <Progress value={(upload.done / upload.total) * 100} />
            <p className="mt-2 text-sm text-zinc-600">Processando {upload.done + 1} de {upload.total}…</p>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="photo-file-input" />
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="admin-photo-grid">
        {photos.map((p) => (
          <div key={p.id} className="group relative aspect-square rounded-md overflow-hidden bg-zinc-100 border border-zinc-200" data-testid={`admin-photo-${p.id}`}>
            <img src={thumbUrl(p.id)} alt="" loading="lazy" className="w-full h-full object-cover" />
            <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/60 text-white text-xs flex items-center gap-1"><ScanFace className="w-3 h-3" />{p.face_count}</span>
            <button onClick={() => editBibs(p)} data-testid={`edit-bibs-${p.id}`} className="absolute top-8 left-1.5 max-w-[90%] px-1.5 py-0.5 rounded bg-white/90 text-xs flex items-center gap-1 truncate">
              <Hash className="w-3 h-3 shrink-0" />{p.bibs_status === "pending" ? "lendo…" : p.bibs?.length ? p.bibs.join(", ") : "sem nº"}
            </button>
            {event.cover_photo_id === p.id && <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-[#0055FF] text-white text-xs">Capa</span>}
            <div className="absolute inset-x-0 bottom-0 p-1.5 flex gap-1.5 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => setCover(p.id)} data-testid={`set-cover-${p.id}`} className="flex-1 h-8 rounded bg-white/90 text-xs font-medium flex items-center justify-center gap-1"><Star className="w-3 h-3" />Capa</button>
              <button onClick={() => delPhoto(p.id)} data-testid={`delete-photo-${p.id}`} className="h-8 w-8 rounded bg-white/90 grid place-items-center"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
            </div>
          </div>
        ))}
      </div>

      {edit && <EventForm open={edit} initial={event} onClose={() => setEdit(false)} onSaved={(d) => setEvent(d)} />}
    </AdminShell>
  );
}
